import React, { useCallback, useEffect, useState } from "react";
import { Button, CircularProgress, MenuItem, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { ConfirmDialog } from "./RunPanel";
import { zb } from "./api";
import styles from "./ZohoBilling.module.css";

const TYPES = [
  { value: "vend_part", label: "Vendor part (Microsoft SKU)", hint: "Stable product key; new monthly IM SKUs resolve through it." },
  { value: "im_sku", label: "IM SKU (override)", hint: "Exact Ingram SKU. Checked first; use only to override a vendor part." },
  {
    value: "description",
    label: "Description keywords",
    hint: "Comma-separated words that must all appear, e.g. GOOGLE, STARTER, ANNUAL. Used for Google products (they share one vendor part). First matching rule wins.",
  },
];
const typeLabel = (t) => (TYPES.find((x) => x.value === t) || {}).label || t;
const EMPTY = { match_type: "vend_part", match_value: "", zoho_item_id: "", zoho_item_name: "", notes: "" };

// An unmapped SKU from a preview -> the most stable way to match it.
export function prefillFromSku(sku) {
  const generic = !sku.vendPart || sku.vendPart === "GWS_ENTITLEMENT";
  return {
    ...EMPTY,
    match_type: generic ? "description" : "vend_part",
    match_value: generic ? "" : sku.vendPart,
    notes: `${sku.description}${sku.imSku ? ` (IM SKU ${sku.imSku})` : ""}`,
  };
}

export default function SkuMappingTab({ prefill, onPrefillUsed }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [item, setItem] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await zb("/sku-mappings")).mappings);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (prefill) {
      setForm(prefill);
      setEditingId(null);
      setItem(null);
      onPrefillUsed();
    }
  }, [prefill, onPrefillUsed]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (k === "zoho_item_id") setItem(null);
  };

  const check = async () => {
    setChecking(true);
    try {
      const it = await zb(`/zoho-items/${encodeURIComponent(form.zoho_item_id.trim())}`);
      setItem(it);
      if (!form.zoho_item_name) setForm((f) => ({ ...f, zoho_item_name: it.name }));
    } catch (err) {
      setItem(null);
      toast.error(err.message);
    } finally {
      setChecking(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await zb(`/sku-mappings/${editingId}`, { method: "PUT", body: form });
      else await zb("/sku-mappings", { method: "POST", body: form });
      toast.success("Mapping saved. Preview the statement again to use it.");
      setForm(EMPTY);
      setEditingId(null);
      setItem(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const edit = (r) => {
    setEditingId(r.id);
    setItem(null);
    setForm({
      match_type: r.match_type,
      match_value: r.match_value,
      zoho_item_id: r.zoho_item_id,
      zoho_item_name: r.zoho_item_name || "",
      notes: r.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async () => {
    const r = deleting;
    setDeleting(null);
    try {
      await zb(`/sku-mappings/${r.id}`, { method: "DELETE" });
      toast.success("Mapping deleted");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const hint = (TYPES.find((t) => t.value === form.match_type) || {}).hint;
  const q = filter.trim().toUpperCase();
  const shown = q
    ? rows.filter((r) => `${r.match_value} ${r.zoho_item_id} ${r.zoho_item_name}`.toUpperCase().includes(q))
    : rows;

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>{editingId ? `Edit mapping #${editingId}` : "Map an Ingram product to a Zoho item"}</div>
        <div className={styles.muted}>
          A bill with any unmapped product is never posted — SKUs are never guessed. The Zoho item id is checked against Zoho
          Books before saving.
        </div>
        <div className={styles.formGrid}>
          <TextField select size="small" label="Match on" value={form.match_type} onChange={set("match_type")}>
            {TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label={form.match_type === "description" ? "Keywords (comma-separated)" : "Value"}
            value={form.match_value}
            onChange={set("match_value")}
          />
          <div className={styles.inline}>
            <TextField size="small" label="Zoho item id" value={form.zoho_item_id} onChange={set("zoho_item_id")} fullWidth />
            <Button
              size="small"
              variant="outlined"
              disabled={!form.zoho_item_id.trim() || checking}
              onClick={check}
              sx={{ textTransform: "none" }}
            >
              {checking ? <CircularProgress size={14} /> : "Check"}
            </Button>
          </div>
          <TextField size="small" label="Item name (optional)" value={form.zoho_item_name} onChange={set("zoho_item_name")} />
          <TextField size="small" label="Notes" value={form.notes} onChange={set("notes")} className={styles.span2} />
        </div>
        {hint && <div className={styles.small}>{hint}</div>}
        {item && (
          <div className={item.can_be_purchased ? styles.okBox : styles.warnBox}>
            Zoho item <strong>{item.name}</strong> ({item.status})
            {item.can_be_purchased
              ? " — can be purchased."
              : " — sales-only right now; posting turns on “I purchase this” automatically."}
          </div>
        )}
        <div className={styles.actions} style={{ marginTop: 12 }}>
          {editingId && (
            <Button
              onClick={() => {
                setEditingId(null);
                setForm(EMPTY);
                setItem(null);
              }}
              sx={{ textTransform: "none" }}
            >
              Cancel edit
            </Button>
          )}
          <Button
            className={styles.primaryBtn}
            variant="contained"
            disabled={saving || !form.match_value.trim() || !form.zoho_item_id.trim()}
            onClick={save}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : "Save mapping"}
          </Button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.tableHead}>
          <strong>
            {rows.length} mapping(s) {loading && <CircularProgress size={12} />}
          </strong>
          <TextField size="small" placeholder="Search" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Match on</th>
                <th>Value</th>
                <th>Zoho item</th>
                <th>Notes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td className={styles.small}>{typeLabel(r.match_type)}</td>
                  <td className={styles.mono}>{r.match_value}</td>
                  <td>
                    {r.zoho_item_name || "—"}
                    <div className={`${styles.mono} ${styles.small}`}>{r.zoho_item_id}</div>
                  </td>
                  <td className={styles.small}>{r.notes || "—"}</td>
                  <td className={styles.rowActions}>
                    <Button size="small" onClick={() => edit(r)} sx={{ textTransform: "none" }}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => setDeleting(r)} sx={{ textTransform: "none" }}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this mapping?"
        confirmLabel="Delete"
        onClose={() => setDeleting(null)}
        onConfirm={remove}
      >
        {deleting && (
          <p>
            Bills with <span className={styles.mono}>{deleting.match_value}</span> will be blocked until it is mapped again.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
